import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { discussionService } from '../services/api';
import { Discussion } from '../types';
import { formatLocalRange, formatLocalTimestamp, formatUtcRange } from '../utils/discussionTime';
import DiscussionAreaViewer from '../components/DiscussionAreaViewer';
import MesoscaleGraphic from '../components/MesoscaleGraphic';
import DiscussionStaffPanel from '../components/DiscussionStaffPanel';

const STATE_NAMES: Record<string, string> = { WI: 'Wisconsin', MI: 'Michigan', OH: 'Ohio', IL: 'Illinois', IN: 'Indiana' };

const DiscussionDetailPage: React.FC = () => {
  const { permalink } = useParams<{ permalink: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [discussion, setDiscussion] = useState<Discussion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedPost, setCopiedPost] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!permalink) return;

    setLoading(true);
    discussionService
      .getByPermalink(permalink)
      .then((res: { data: Discussion }) => {
        setDiscussion(res.data);
        setError(null);
      })
      .catch((requestError: any) => {
        if (requestError.response?.status === 404) {
          setError('Discussion not found');
          return;
        }
        setError(requestError.response?.data?.error || 'Unable to load discussion. Check the API connection and try again.');
      })
      .finally(() => setLoading(false));
  }, [permalink]);

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-center p-4 text-red-600">{error}</div>;
  if (!discussion) return <div className="text-center p-4">Discussion not found</div>;

  const publishedDate = formatLocalTimestamp(discussion.published_at || discussion.created_at);
  const archivedDate = formatLocalTimestamp(discussion.archived_at);
  const validUtcRange = formatUtcRange(discussion.valid_from_utc, discussion.valid_to_utc);
  const validLocalRange = formatLocalRange(discussion.valid_from_utc, discussion.valid_to_utc);
  const canDelete = !!user?.permissions?.includes('manage_any_discussion');
  const isAuthor = !!user && String(user.id) === String(discussion.author_id);
  const showStaffTools = isAuthor || !!user?.permissions?.includes('view_unpublished_discussions');
  const canCreate = !!user?.permissions?.includes('create_discussions');

  const handleCopyPermalink = async () => {
    const url = `${window.location.origin}/discussions/${discussion.permalink}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleCopyXPost = async () => {
    const url = `${window.location.origin}/discussions/${discussion.permalink}`;
    const concern = (discussion.concerning_text || '').trim();
    const concernLine = concern ? `${concern}\n` : '';

    const dynamicTags = [
      ...(discussion.hazard_tags || []),
      ...(discussion.tags || []),
    ]
      .map((tag) => tag.replace(/[^a-zA-Z0-9]/g, ''))
      .filter(Boolean)
      .slice(0, 4)
      .map((tag) => `#${tag}`);

    const baseTags = ['#GLSN', '#weather'];
    const hashtags = Array.from(new Set([...dynamicTags, ...baseTags])).join(' ');

    const postText = `${discussion.title}\n${concernLine}${url}\n${hashtags}`.trim();
    await navigator.clipboard.writeText(postText);
    setCopiedPost(true);
    window.setTimeout(() => setCopiedPost(false), 1800);
  };

  const handleDelete = async () => {
    if (!canDelete || !discussion) {
      return;
    }

    const confirmed = window.confirm(`Delete discussion "${discussion.title}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await discussionService.delete(discussion.id);
      navigate('/archive');
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to delete discussion');
      setDeleting(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="container detail-container">
        <article className="card detail-card printable-area">
          {error && <div className="alert alert-error print-hidden">{error}</div>}
          <div className="detail-header">
            <div>
              <p className="eyebrow">Discussion</p>
              <h1 className="page-heading">{discussion.title}</h1>
            </div>
            <div className="hero-actions print-hidden">
              {canDelete && (
                <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete discussion'}
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={handleCopyPermalink}>
                {copied ? 'Copied link' : 'Copy permalink'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCopyXPost}>
                {copiedPost ? 'Copied X post' : 'Copy X post'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => window.print()}>
                Print view
              </button>
            </div>
          </div>

          <div className="detail-meta">
            <p><strong>Published:</strong> {publishedDate}</p>
            {archivedDate && <p><strong>Archived:</strong> {archivedDate}</p>}
            {validUtcRange && <p><strong>Valid (Z):</strong> {validUtcRange}</p>}
            {validLocalRange && <p><strong>Local time:</strong> {validLocalRange}</p>}
            <p><strong>Views:</strong> {discussion.views}</p>
            <p><strong>Permalink:</strong> <code>{discussion.permalink}</code></p>
            <p><strong>Status:</strong> {discussion.status}</p>
          </div>

          {(discussion.tornado_probability || discussion.tornado_peak_intensity || discussion.wind_probability || discussion.hail_probability) && (
            <div className="detail-chip-groups">
              <div>
                <p className="detail-subheading">Most Probable Peak Intensity</p>
                <div className="chip-row">
                  {discussion.tornado_probability && <span className="chip chip-static">Tornado {discussion.tornado_probability}</span>}
                  {discussion.wind_probability && <span className="chip chip-static">Wind {discussion.wind_probability}</span>}
                  {discussion.hail_probability && <span className="chip chip-static">Hail {discussion.hail_probability}</span>}
                </div>
              </div>
            </div>
          )}

          {(discussion.area_geojson || discussion.tornado_probability || discussion.wind_probability || discussion.hail_probability) && (
            <div className="detail-chip-groups">
              <div>
                <p className="detail-subheading">Mesoscale graphic</p>
                <MesoscaleGraphic
                  title={discussion.title}
                  validUntil={discussion.valid_to_utc}
                  validFrom={discussion.valid_from_utc}
                  issuedAt={discussion.published_at || discussion.created_at}
                  issuedBy={discussion.author_name || null}
                  mdNumber={discussion.md_number}
                  summary={discussion.summary || discussion.content?.split(/\n\s*\n/)[0] || null}
                  areasAffected={discussion.area_check?.states?.length ? discussion.area_check.states.map((s) => STATE_NAMES[s] || s).join(', ') : null}
                  concerning={discussion.concerning_text || null}
                  tornadoProbability={discussion.tornado_probability}
                  windProbability={discussion.wind_probability}
                  hailProbability={discussion.hail_probability}
                  areaGeojson={discussion.area_geojson}
                  highlightColor={discussion.highlight_color}
                />
              </div>
            </div>
          )}

          {(discussion.tags.length > 0 || (discussion.hazard_tags || []).length > 0) && (
            <div className="detail-chip-groups">
              {discussion.tags.length > 0 && (
                <div>
                  <p className="detail-subheading">Workflow tags</p>
                  <div className="chip-row">
                    {discussion.tags.map((tag) => (
                      <span key={tag} className="chip chip-static">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {(discussion.hazard_tags || []).length > 0 && (
                <div>
                  <p className="detail-subheading">Hazards</p>
                  <div className="chip-row">
                    {(discussion.hazard_tags || []).map((tag) => (
                      <span key={tag} className="chip chip-active chip-static">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Areas affected */}
          <div className="detail-chip-groups">
            <div>
              <p className="detail-subheading">Areas affected</p>
              {discussion.area_geojson ? (
                <DiscussionAreaViewer value={discussion.area_geojson} highlightColor={discussion.highlight_color} />
              ) : (
                <p className="detail-content muted">No area specified.</p>
              )}
            </div>
          </div>

          {/* Concerning */}
          <div className="detail-chip-groups">
            <div>
              <p className="detail-subheading">Concerning</p>
              <div className="prose">
                <p className="detail-content">{discussion.concerning_text || '—'}</p>
              </div>
            </div>
          </div>

          {/* SUMMARY */}
          <div className="detail-chip-groups">
            <div>
              <p className="detail-subheading">SUMMARY</p>
              <div className="prose">
                <p className="detail-content">
                  {discussion.summary || discussion.content?.split(/\n\s*\n/)[0] || 'No summary provided.'}
                </p>
              </div>
            </div>
          </div>

          {/* DISCUSSION */}
          <div className="detail-chip-groups">
            <div>
              <p className="detail-subheading">DISCUSSION</p>
              <div className="prose detail-body">
                <p className="detail-content">
                  {discussion.discussion_body || discussion.content?.split(/\n\s*\n/).slice(1).join('\n\n') || 'No discussion provided.'}
                </p>
              </div>
            </div>
          </div>
        </article>
        {showStaffTools && <DiscussionStaffPanel discussion={discussion} canCreate={canCreate} />}
      </div>
    </div>
  );
};

export default DiscussionDetailPage;
